import axios from 'axios';
import {API_ISPM_BASE_URL,API_ITDR_BASE_URL,ISPM_Rule_Engine_API_BASE_URL} from "../utils/constants"

// ============================================
// TOKEN HELPERS
// ============================================

export const getToken = () => {
  try {
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser) {
      const parsed = JSON.parse(currentUser);
      return parsed?.token || null;
    }
    return null;
  } catch {
    return null;
  }
};

export const setToken = (token, username, role,userId) => {
  try {
    const userData = {
      token,
      userName: username,
      role: role,
      userId:userId,
    };
    localStorage.setItem('currentUser', JSON.stringify(userData));
  } catch (error) {
    console.error('Error storing token:', error);
  }
};

export const removeToken = () => {
  localStorage.removeItem('currentUser');
  localStorage.removeItem('isFirstTime');
  localStorage.removeItem('expired');
  localStorage.removeItem('username');
};

export const isAuthenticated = () => {
  const token = getToken();
  return !!token;
};

// ============================================
// HANDLE TOKEN EXPIRY
// ============================================

export const handleTokenExpiry = () => {
  console.warn('Session expired. Redirecting to login...');
  removeToken();
  window.location.href = '/login';
};

// ============================================
// MAIN AXIOS INSTANCE (Recommended)
// Use this for all API calls - token auto-attached
// ============================================

export const api = axios.create({
  baseURL: API_ISPM_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Request Interceptor ───────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response Interceptor ──────────────────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;

    if (status === 401) {
      handleTokenExpiry();
      return Promise.reject(new Error('Unauthorized - Session expired'));
    }

    if (status === 403) {
      console.warn('Forbidden. You do not have permission.');
    }

    if (status === 500) {
      console.error('Internal server error.');
    }

    return Promise.reject(error);
  }
);

// ============================================
// ITDR AXIOS INSTANCE
// For ITDR external API - token auto-attached
// ============================================

export const itdrApi = axios.create({
  baseURL: API_ITDR_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── ITDR Request Interceptor ──────────────────────
itdrApi.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── ITDR Response Interceptor ─────────────────────
itdrApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401) {
      handleTokenExpiry();
    }
    return Promise.reject(error);
  }
);

// ============================================
// ISPM/RULE ENGINE AXIOS INSTANCE
// For external services - token auto-attached
// ============================================

export const ispmApi = axios.create({
  baseURL: ISPM_Rule_Engine_API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── ISPM Request Interceptor ──────────────────────
ispmApi.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── ISPM Response Interceptor ─────────────────────
ispmApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401) {
      handleTokenExpiry();
    }
    return Promise.reject(error);
  }
);

// ============================================
// FACTORY FUNCTION FOR CUSTOM BASE URLS
// Use when you have a unique API endpoint
// ============================================

export const createAxiosInstance = (baseURL, instanceName = 'custom') => {
  const instance = axios.create({
    baseURL,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  instance.interceptors.request.use(
    (config) => {
      const token = getToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      if (status === 401) {
        handleTokenExpiry();
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

export default api;